/**
 * Pure helpers for persisting trigger-shot intent on the cycle's protocol
 * phase (#2224).
 *
 * The medications API has no role field; the durable representation of a
 * trigger shot is `phase.triggerShot` on the protocol phase — the record the
 * stim response chart, cycle header, past-cycle detail, and hub step
 * progress already read. The Add Medication trigger flow used to keep the
 * role client-side only, so a reloaded app couldn't tell a trigger shot from
 * a standard medication. These builders are extracted so the contract is
 * testable without React Native imports.
 */

export interface PhaseTriggerShot {
  id?: string;
  date?: string | null;
  time?: string | null;
  notes?: string | null;
}

export interface TriggerShotPhaseCandidate {
  id: string;
  type?: string | null;
  triggerShot?: PhaseTriggerShot | null;
}

/**
 * Pick the phase whose `triggerShot` should carry a newly saved trigger-shot
 * medication: the explicitly linked phase when the caller has one, otherwise
 * the cycle's protocol phase (the only phase type the step editor writes
 * trigger data to). Returns null when neither exists — an unlinked trigger
 * med has no durable home and the caller must not pretend otherwise.
 */
export function resolveTriggerShotPhase(
  phases: readonly TriggerShotPhaseCandidate[],
  linkedPhaseId?: string | null,
): TriggerShotPhaseCandidate | null {
  if (linkedPhaseId) {
    const linked = phases.find((p) => p.id === linkedPhaseId);
    if (linked) return linked;
  }
  return phases.find((p) => p.type === "protocol") ?? null;
}

/**
 * Build the `triggerShot` PATCH body for a trigger-shot medication save.
 * Mirrors the step-editor convention (`buildPhaseSubObjects`): wall-clock
 * date + optional HH:mm time, medication name in `notes`, and the existing
 * record's id preserved so the backend updates instead of inserting a
 * duplicate.
 */
export function buildTriggerShotPatch(args: {
  existing?: PhaseTriggerShot | null;
  startDate: string;
  time?: string | null;
  medicationName?: string | null;
}): PhaseTriggerShot {
  const patch: PhaseTriggerShot = { date: args.startDate };
  if (args.existing?.id) patch.id = args.existing.id;
  if (args.time) patch.time = args.time;
  const name = args.medicationName?.trim();
  if (name) patch.notes = name;
  else if (args.existing?.notes) patch.notes = args.existing.notes;
  return patch;
}
